/**
 * EventPayment Component - Versione aggiornata
 * Gestisce il pagamento per l'iscrizione a un evento
 * Supporta: Stripe (online) e Bonifico bancario (con upload ricevuta)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Button, Alert, Spinner, Form, Nav, Badge, Table } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Inizializza Stripe
let stripePromise = null;

const getStripeConfig = async () => {
  try {
    const response = await api.get('/payments/config');
    if (response.data.success && response.data.data.publishableKey) {
      stripePromise = loadStripe(response.data.data.publishableKey);
    }
  } catch (error) {
    console.error('Error loading Stripe config:', error);
  }
};

getStripeConfig();

/**
 * Form pagamento con Stripe
 */
const StripePaymentForm = ({ paymentRequest, onSuccess, onError }) => {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // Crea Payment Intent
      const intentResponse = await api.post('/payments/create-intent', {
        eventId: paymentRequest.event_id,
        associationId: paymentRequest.association_id,
        amount: parseFloat(paymentRequest.amount),
        description: paymentRequest.description
      });

      if (!intentResponse.data.success) {
        throw new Error(intentResponse.data.message);
      }

      const { clientSecret } = intentResponse.data.data;

      // Conferma pagamento
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: user.username,
            email: user.email
          }
        }
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <Form.Label>Dettagli carta di credito/debito</Form.Label>
        <div className="p-3 border rounded">
          <CardElement options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' }
              },
              invalid: { color: '#9e2146' }
            }
          }} />
        </div>
        <Form.Text className="text-muted">
          Il pagamento è sicuro e crittografato tramite Stripe
        </Form.Text>
      </div>

      <Button
        type="submit"
        disabled={!stripe || loading}
        style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
        className="w-100"
        size="lg"
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Elaborazione...
          </>
        ) : (
          <>
            <i className="bi bi-credit-card me-2"></i>
            Paga €{parseFloat(paymentRequest.amount).toFixed(2)}
          </>
        )}
      </Button>
    </Form>
  );
};

/**
 * Form upload bonifico bancario
 */
const BankTransferUploadForm = ({ paymentRequest, onSuccess, onError }) => {
  const [file, setFile] = useState(null);
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Verifica dimensione (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('Il file è troppo grande. Dimensione massima: 10MB');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Seleziona un file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('receipt', file);
      formData.append('paymentRequestId', paymentRequest.id);
      formData.append('bankTransferReference', reference);
      formData.append('amount', paymentRequest.amount);

      const response = await api.post('/payments/bank-transfer/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        onSuccess(response.data.data);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || err.message || 'Errore durante il caricamento');
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}

      <Alert variant="info">
        <h6><i className="bi bi-info-circle me-2"></i>Istruzioni per il bonifico:</h6>
        <Table size="sm" className="mb-0">
          <tbody>
            <tr>
              <td><strong>Beneficiario:</strong></td>
              <td>[Da configurare - Nome ente UISP]</td>
            </tr>
            <tr>
              <td><strong>IBAN:</strong></td>
              <td>[Da configurare - IBAN]</td>
            </tr>
            <tr>
              <td><strong>Causale:</strong></td>
              <td>Evento {paymentRequest.event_id} - Associazione</td>
            </tr>
            <tr>
              <td><strong>Importo:</strong></td>
              <td><strong>€{parseFloat(paymentRequest.amount).toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </Table>
      </Alert>

      <Form.Group className="mb-3">
        <Form.Label>
          Causale / Riferimento bonifico <span className="text-muted">(opzionale)</span>
        </Form.Label>
        <Form.Control
          type="text"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Es: Riferimento operazione, CRO, TRN"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          Ricevuta di pagamento <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          required
        />
        <Form.Text className="text-muted">
          Carica un PDF o un'immagine della ricevuta di bonifico (max 10MB)
        </Form.Text>
        {file && (
          <Alert variant="success" className="mt-2 mb-0">
            <i className="bi bi-file-earmark-check me-2"></i>
            File selezionato: {file.name} ({(file.size / 1024).toFixed(0)} KB)
          </Alert>
        )}
      </Form.Group>

      <Alert variant="warning">
        <strong>⚠️ Importante:</strong> Il pagamento sarà verificato manualmente dal responsabile.
        Riceverai una conferma quando il bonifico sarà stato verificato.
      </Alert>

      <Button
        type="submit"
        disabled={loading || !file}
        style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
        className="w-100"
        size="lg"
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Caricamento...
          </>
        ) : (
          <>
            <i className="bi bi-upload me-2"></i>
            Carica ricevuta bonifico
          </>
        )}
      </Button>
    </Form>
  );
};

/**
 * Componente principale EventPayment
 */
const EventPayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer'); // default: bonifico
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Recupera evento
      const eventResponse = await api.get(`/events/${id}`);
      setEvent(eventResponse.data.event || eventResponse.data);

      // Verifica se c'è una richiesta di pagamento specifica
      const requestId = searchParams.get('request');
      if (requestId) {
        const requestResponse = await api.get(`/payments/requests/${requestId}`);
        if (requestResponse.data.success) {
          setPaymentRequest(requestResponse.data.data);
        }
      } else {
        // Altrimenti cerca richieste per questa associazione e evento
        const requestsResponse = await api.get(
          `/payments/requests/association/${user.association_id}`
        );
        if (requestsResponse.data.success) {
          const pendingRequest = requestsResponse.data.data.find(
            r => r.event_id === parseInt(id) && 
                 ['awaiting_payment', 'pending'].includes(r.status)
          );
          if (pendingRequest) {
            setPaymentRequest(pendingRequest);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Impossibile caricare i dati del pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (data) => {
    setPaymentData(data);
    setSuccess(true);
  };

  const handlePaymentError = (err) => {
    setError(err.message || 'Errore durante il pagamento');
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" style={{ color: '#006e39' }} />
        <p className="mt-3">Caricamento...</p>
      </Container>
    );
  }

  if (error && !event) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Errore</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate(-1)}>
            Torna indietro
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!paymentRequest) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Nessuna richiesta di pagamento</Alert.Heading>
          <p>Non ci sono richieste di pagamento per questo evento.</p>
          <Button variant="outline-warning" onClick={() => navigate(-1)}>
            Torna indietro
          </Button>
        </Alert>
      </Container>
    );
  }

  if (success) {
    const isStripe = paymentData?.id; // Payment Intent ha un id
    
    return (
      <Container className="mt-4">
        <Card className="shadow-sm">
          <Card.Body className="text-center p-5">
            <div className="mb-4">
              <i className="bi bi-check-circle" style={{ fontSize: '4rem', color: '#28a745' }}></i>
            </div>
            
            <h2 style={{ color: '#006e39' }}>
              {isStripe ? 'Pagamento completato!' : 'Ricevuta caricata!'}
            </h2>
            
            <p className="lead">
              {isStripe 
                ? 'Il tuo pagamento è stato elaborato con successo.'
                : 'La ricevuta del bonifico è stata caricata. Sarà verificata dal responsabile.'}
            </p>
            
            <Alert variant={isStripe ? 'success' : 'info'} className="mt-4">
              {isStripe ? (
                <>
                  <div className="d-flex justify-content-between mb-2">
                    <span>ID Transazione:</span>
                    <strong>{paymentData.id}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Importo:</span>
                    <strong>€{(paymentData.amount / 100).toFixed(2)}</strong>
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-2">
                    <i className="bi bi-clock me-2"></i>
                    <strong>Stato:</strong> In attesa di verifica
                  </p>
                  <p className="mb-0">
                    Riceverai una notifica quando il bonifico sarà stato verificato dal responsabile.
                  </p>
                </>
              )}
            </Alert>

            <div className="mt-4">
              <Button
                style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
                size="lg"
                onClick={() => navigate('/association')}
              >
                Torna alla dashboard
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="mb-4">
        <Button
          variant="link"
          className="ps-0"
          onClick={() => navigate(-1)}
          style={{ color: '#006e39' }}
        >
          <i className="bi bi-arrow-left me-2"></i>
          Torna indietro
        </Button>
      </div>

      {/* Dettagli richiesta pagamento */}
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
          <h5 className="mb-0">Richiesta di Pagamento</h5>
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <p className="mb-2"><strong>Evento:</strong> {event?.title}</p>
              <p className="mb-2">
                <strong>Data evento:</strong>{' '}
                {new Date(event?.event_date || event?.eventDate).toLocaleDateString('it-IT')}
              </p>
            </div>
            <div className="col-md-6 text-md-end">
              <h3 style={{ color: '#006e39' }}>
                €{parseFloat(paymentRequest.amount).toFixed(2)}
              </h3>
              {paymentRequest.payment_deadline && (
                <p className="text-muted">
                  Scadenza:{' '}
                  {new Date(paymentRequest.payment_deadline).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
          </div>

          {paymentRequest.description && (
            <Alert variant="info" className="mt-3 mb-0">
              <strong>Note:</strong> {paymentRequest.description}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Scelta metodo di pagamento */}
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-3">Seleziona metodo di pagamento</h5>
          <Nav variant="pills" activeKey={paymentMethod} onSelect={setPaymentMethod}>
            <Nav.Item>
              <Nav.Link eventKey="bank_transfer" className="me-2">
                <i className="bi bi-bank me-2"></i>
                Bonifico Bancario
                <Badge bg="success" className="ms-2">Consigliato</Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="stripe">
                <i className="bi bi-credit-card me-2"></i>
                Carta di Credito/Debito
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        <Card.Body className="p-4">
          {paymentMethod === 'stripe' && stripePromise ? (
            <Elements stripe={stripePromise}>
              <StripePaymentForm
                paymentRequest={paymentRequest}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>
          ) : (
            <BankTransferUploadForm
              paymentRequest={paymentRequest}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EventPayment;
